if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.selectedIndex = this.selectedIndex;
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.containerWidth = this.dataset.container;
        this.cleanupScrollAnimations = [];
        this.enableZoom = this.dataset.enableZoom ? this.dataset.enableZoom === 'true' : true;
        this.borderRadius = this.dataset.borderRadius;
        this.zoom = 0;

        this.isHeroSlider = this.classList.contains('hero-slider');

        if (!this.enableSlider) {
          if (!this.isHeroSlider) {
            this.setupSlideHeightSync();
          } else {
            this.setupHeroImageLoadListeners();
          }
          return;
        }

        this.setupZoom();
        window.addEventListener('resize', this.setupZoom.bind(this));

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      setupZoom() {
        if (!this.enableZoom) return;

        this.destroyZoom();

        const pageWidthEl = this.querySelector('.page-width');
        const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
        this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), window.innerWidth * 0.04);

        if (this.zoom > 15) {
          this.initZoom();
        }
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          autoHeight: this.isHeroSlider && this.classList.contains('hero-slider--mobile-cover'),
          on: {
            init: (swiper) => {
              this.handleAfterInit();
              if (this.isHeroSlider) {
                this.setupHeroImageLoadListeners();
                this.setupHeroSliderHeight(swiper);
              }
            },
            ...(this.isHeroSlider
              ? {
                  slideChangeTransitionEnd: (swiper) => this.setupHeroSliderHeight(swiper),
                  resize: (swiper) => this.setupHeroSliderHeight(swiper),
                }
              : { resize: this.syncSlideHeights.bind(this) }),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector('.swiper-button-next'),
            prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
          };
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (!this.isHeroSlider) {
          this.setupSlideHeightSync();
        }

        if (this.sliderInstance) {
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button') && !element.classList.contains('btn')) {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideToLoop(this.sliderInstance.slider.slides.indexOf(slide));
              });
            }
          });
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              motionEl && motionEl.refreshAnimation();
            });
          });
        }
      }

      setupHeroSliderHeight(swiper) {
        if (!this.isHeroSlider || !this.classList.contains('hero-slider--mobile-contain')) return;

        const slider = swiper || this.sliderInstance?.slider;
        if (!slider?.wrapperEl) return;

        if (window.matchMedia('(min-width: 768px)').matches) {
          slider.wrapperEl.style.height = '';
          slider.el.style.height = '';
          return;
        }

        const slide = slider.slides[slider.activeIndex];
        const inner = slide?.querySelector('.hero-slider__slide-inner');
        if (!inner) return;

        const height = Math.ceil(inner.getBoundingClientRect().height);
        if (height > 0) {
          slider.wrapperEl.style.height = `${height}px`;
          slider.el.style.height = `${height}px`;
        }
      }

      setupHeroImageLoadListeners() {
        if (!this.isHeroSlider) return;

        this.querySelectorAll('.hero-slider__picture img').forEach((img) => {
          const refresh = () => {
            const swiper = this.sliderInstance?.slider;
            if (swiper) this.setupHeroSliderHeight(swiper);
          };

          if (img.complete) {
            refresh();
          } else {
            img.addEventListener('load', refresh, { once: true });
          }
        });
      }

      syncSlideHeights() {
        if (this.isHeroSlider) return;

        const swiper = this.sliderInstance?.slider;

        if (swiper) {
          swiper.updateAutoHeight(0);
        }

        const activeSlide = swiper?.slides?.[swiper.activeIndex] || this.querySelector('.swiper-slide');
        if (!activeSlide) return;

        const img = activeSlide.querySelector('.slideshow__picture img');
        if (!img?.naturalWidth) return;

        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const styles = getComputedStyle(activeSlide);
        const aspectRatio = isMobile
          ? parseFloat(styles.getPropertyValue('--slide-aspect-ratio-mobile')) ||
            parseFloat(styles.getPropertyValue('--slide-aspect-ratio'))
          : parseFloat(styles.getPropertyValue('--slide-aspect-ratio'));

        const naturalRatio = img.naturalWidth / img.naturalHeight;
        if (!Number.isFinite(aspectRatio) || Math.abs(naturalRatio - aspectRatio) > 0.02) {
          const prop = isMobile ? '--slide-aspect-ratio-mobile' : '--slide-aspect-ratio';
          activeSlide.style.setProperty(prop, naturalRatio);
          swiper?.updateAutoHeight(0);
        }
      }

      setupSlideHeightSync() {
        if (this.isHeroSlider) return;

        this._onWindowResize = () => this.syncSlideHeights();

        this.querySelectorAll('.slideshow__picture img').forEach((img) => {
          const onImageReady = () => {
            const slide = img.closest('.swiper-slide');
            if (!slide || !img.naturalWidth) return;

            const naturalRatio = img.naturalWidth / img.naturalHeight;
            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            const prop = isMobile ? '--slide-aspect-ratio-mobile' : '--slide-aspect-ratio';
            slide.style.setProperty(prop, naturalRatio);

            this.syncSlideHeights();
          };

          if (img.complete) {
            onImageReady();
          } else {
            img.addEventListener('load', onImageReady, { once: true });
          }
        });

        window.addEventListener('resize', this._onWindowResize);
        this.syncSlideHeights();
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');
        if (!this.isHeroSlider) {
          this.syncSlideHeights();
        }

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
        if (!this.isHeroSlider) {
          this.syncSlideHeights();
        }
      }

      disconnectedCallback() {
        if (this._onWindowResize) {
          window.removeEventListener('resize', this._onWindowResize);
        }
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  setTimeout(() => {
                    motionEl && motionEl.refreshAnimation();
                  });
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      initZoom() {
        if (this.containerWidth !== 'full') return;

        const motionTarget = {
          target: this.section,
          offset: [
            [-0.1, 0],
            [0.7, 0],
          ],
        };

        this.querySelectorAll('.slideshow__item-wrap').forEach((slide) => {
          const slideBg = slide.querySelector('.slideshow__bg');

          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(slideBg, {
                transform: [`scale(1) translateZ(0)`, `scale(1.1) translateZ(0)`],
              }),
              motionTarget
            )
          );
        });

        this.cleanupScrollAnimations.push(
          FoxTheme.Motion.scroll(
            FoxTheme.Motion.animate(this, {
              clipPath: [
                `inset(0%)`,
                `inset(${Math.min(50, this.zoom)}px ${this.zoom}px round ${this.borderRadius}px)`,
              ],
            }),
            motionTarget
          )
        );

        if (this.sliderControls) {
          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(this.sliderControls, {
                bottom: [`0`, `${Math.min(50, this.zoom)}px`],
              }),
              motionTarget
            )
          );
        }
      }

      destroyZoom() {
        this.cleanupScrollAnimations.forEach((fn) => fn());
        this.cleanupScrollAnimations = [];
        this.style.clipPath = 'none';
        if (this.sliderControls) this.sliderControls.style.bottom = 0;
      }
    }
  );
}
